import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { BuildSearchResult } from '../../../../models/Build';
import {
    DefaultButton,
    IButtonStyles,
    IStackStyles,
    IStackTokens,
    IconButton,
    Link,
    Panel,
    PanelType,
    Stack,
    Text,
} from '@fluentui/react';
import { getBuildDetailsPath } from '../../../../util/BuildUtils';
import { CancelToken } from 'axios';
import { AuthContext } from '../../../../../HubLayout/models/AuthContext';
import { setUserProfileData } from '../../../../../Common/api/ABHub/UserProfileApi';
import { ThemeContext } from '../../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../../Common/util/localStorageUtils';
import { dateLocaleStringWithTimeZone } from '../../../../../Common/util/DateUtils';
import { MultiStageBuildSearch } from '../../MultiStageBuildSearch';
import { ServiceNameDropDown } from '../../../../../Common/components/ServiceNameDropDown';

interface IMobileBuildListProps {
    list: BuildSearchResult[];
    isLoaded: boolean;
    pageNumber?: number;
    hideButton?: boolean;
    setSearchValue?: Dispatch<SetStateAction<string | undefined>>;
    selectedServices: string[];
    setSelectedServices?: Dispatch<SetStateAction<string[]>>;
    setSelectedEnvironments?: Dispatch<SetStateAction<string[]>>;
    setSelectedDeploymentTypes?: Dispatch<SetStateAction<string[]>>;
    setSelectedProjects?: Dispatch<SetStateAction<string[]>>;
    setPageNumber?: Dispatch<SetStateAction<number>>;
    isHomePage?: boolean;
}

export const MobileBuildList: React.FC<IMobileBuildListProps> = (
    props: IMobileBuildListProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const [items, setItems] = useState<BuildSearchResult[]>([]);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const { authContext, setDbUpdated } = useContext(AuthContext);
    const stackTokens: IStackTokens = { childrenGap: 20 };

    const stackStyles: Partial<IStackStyles> = {
        root: {
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.neutralLighter,
            width: '100%',
        },
    };

    const loadMoreButtonStyles: IButtonStyles = {
        root: {
            display: props.hideButton ? 'none' : 'block',
            width: '100%',
            marginTop: '10px',
            background: theme.palette.neutralLighter,
            color: theme.palette.neutralPrimary,
        },
        rootHovered: {
            color: theme.palette.neutralPrimaryAlt,
            backgroundColor: theme.palette.neutralLight,
        },
    };

    // When parent component sends new data, refresh my list accordingly
    useEffect(() => {
        setItems(props.list);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.list, props.isLoaded]);

    const loadMore = () => {
        if (props.setPageNumber && props.pageNumber) {
            props.setPageNumber(props.pageNumber + 1);
        }
    };

    const showFilterComponents = () => {
        setShowFilters(!showFilters);
    };

    return (
        <div className="MobileBuildList-root">
            {!props.isHomePage && (
                <IconButton
                    iconProps={{ iconName: 'Filter' }}
                    style={{
                        display: 'flex',
                        color: theme.palette.neutralPrimary,
                        justifyContent: 'flex-end',
                        width: '100%',
                    }}
                    onClick={showFilterComponents}
                    ariaLabel="Show filters"
                    title="Filters"
                />
            )}

            <Panel
                isLightDismiss
                isOpen={showFilters}
                onDismiss={showFilterComponents}
                closeButtonAriaLabel="Close"
                headerText="Filters"
                type={PanelType.custom}
            >
                <Stack tokens={stackTokens} styles={stackStyles}>
                    <MultiStageBuildSearch
                        setSearchValue={props.setSearchValue}
                        setPageNumber={props.setPageNumber}
                        isLoaded={props.isLoaded}
                        isMobile
                    />
                    <ServiceNameDropDown
                        selectedServices={props.selectedServices}
                        setSelectedServices={props.setSelectedServices}
                        setPageNumber={props.setPageNumber}
                        isLoaded={props.isLoaded}
                        isMobile
                    />
                </Stack>
            </Panel>

            {items.map((item) => {
                return (
                    <Link
                        href={
                            '#' +
                            getBuildDetailsPath(item.buildInstanceCorrelationId)
                        }
                    >
                        <Stack
                            key={item.buildInstanceCorrelationId}
                            tokens={stackTokens}
                            horizontal
                            style={{
                                backgroundColor:
                                    theme.palette.neutralQuaternaryAlt,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 10px 10px 20px',
                                marginBottom: '10px',
                                boxShadow: 'inset 6px 0 0 0 ',
                            }}
                        >
                            <Stack
                                className="TextStack"
                                tokens={{ childrenGap: 8 }}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <div>
                                    <Text
                                        variant="medium"
                                        style={{
                                            float: 'right',
                                            paddingTop: '6px',
                                            paddingLeft: '6px',
                                            maxWidth: '20%',
                                        }}
                                    >
                                        {item.environments}
                                    </Text>

                                    <Stack
                                        style={{
                                            float: 'left',
                                            maxWidth: '80%',
                                            overflowWrap: 'break-word',
                                        }}
                                    >
                                        <Text variant="xLarge">
                                            {item.buildComponentName?.trim() !==
                                            ''
                                                ? `${item.buildBranch}/${item.buildNumber}/${item.buildComponentName}`
                                                : `${item.buildBranch}/${item.buildNumber}`}
                                        </Text>
                                        <br />
                                        <Text variant="small">
                                            <strong>Manifest: </strong>
                                            {item.buildCvrpManifestRelativePath}
                                        </Text>
                                        <Text variant="small">
                                            <strong>Hash: </strong>
                                            {item.buildManifestHash}
                                        </Text>
                                        <br />
                                        <Text variant="small">
                                            <strong>Project: </strong>
                                            {item.projectName}
                                        </Text>
                                        <Text variant="small">
                                            <strong>Repo: </strong>
                                            {item.repoName}
                                        </Text>
                                        <br />
                                        <Text
                                            variant="medium"
                                            style={{ fontWeight: 'bolder' }}
                                        >
                                            {item.serviceTreeName ||
                                                item.serviceTreeId}
                                        </Text>
                                    </Stack>
                                </div>

                                <div>
                                    <Text
                                        variant="medium"
                                        style={{
                                            float: 'right',
                                            color: theme.palette
                                                .neutralPrimaryAlt,
                                            paddingLeft: '6px',
                                            maxWidth: '60%',
                                        }}
                                    >
                                        {dateLocaleStringWithTimeZone(
                                            item.createdOn
                                        )}
                                    </Text>
                                </div>
                            </Stack>
                        </Stack>
                    </Link>
                );
            })}

            {!props.isHomePage && (
                <DefaultButton
                    styles={loadMoreButtonStyles}
                    text={'Load More'}
                    onClick={loadMore}
                />
            )}

            {props.isLoaded && (props.list.length < 1 || items.length < 1) && (
                <p>No data found.</p>
            )}
        </div>
    );
};
